/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    SimpleGrid,
    Button,
    Center,
    useMultiStyleConfig,
    Box,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const SizeRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'squareRefinements',
        disabled: false,
    })

    // `selectedFilters` may be the full URL refine object (keyed by attribute id) or a
    // flat array of value strings (legacy callers). Each size subgroup must check its
    // own attribute so that overlapping value strings (e.g. "S" in Clothing Size vs
    // Bottom Size) don't get marked selected across groups.
    const getSelectedValues = (attributeId) => {
        if (Array.isArray(selectedFilters)) return selectedFilters
        const raw = selectedFilters?.[attributeId]
        if (raw == null) return []
        return Array.isArray(raw) ? raw : [raw]
    }

    return filter?.values?.map((valueFilter) => {
        const selectedValues = getSelectedValues(valueFilter.attributeId)

        // Find all available filter values
        const availableValues = valueFilter?.values || []
        const availableValueIds = new Set(availableValues.map((v) => v.value))

        // Find selected filters that are not in the available values list
        const unavailableSelectedFilters = selectedValues.filter(
            (selectedValue) => !availableValueIds.has(selectedValue)
        )

        // Create dummy filter objects for unavailable selected filters
        const dummyFilters = unavailableSelectedFilters.map((value) => {
            // Try to find the original filter data from all filter values
            const originalValue = valueFilter?.values?.find((v) => v.value === value)
            return (
                originalValue || {
                    value: value,
                    label: value,
                    hitCount: 0
                }
            )
        })

        // Combine dummy filters with available filters
        const allValues = [...dummyFilters, ...availableValues]

        const filteredValues = allValues.filter((value) => {
            const isSelected = selectedValues.includes(value.value)
            return value.hitCount > 0 || isSelected
        })
        // Extract swatchContent to prevent it from being passed to DOM element
        const {swatchContent, ...swatchStyles} = styles.swatch || {}

        if (!filteredValues?.length) return null

        return (
            <Box key={valueFilter.attributeId} mb={3}>
                <Text {...styles.groupTitle}>
                    {valueFilter.label}
                </Text>
                <SimpleGrid templateColumns="repeat(6, 1fr)" spacing={1}>
                    {filteredValues.map((value) => {
                        const isSelected = selectedValues.includes(value.value)

                        return (
                            <Button
                                className="refinements-size-swatch"
                                {...swatchStyles}
                                sx={{
                                    ...swatchContent,
                                    ...(isSelected && {
                                        borderColor: 'webTheme.backgroundReverse',
                                        backgroundColor: 'webTheme.backgroundReverse'
                                    })
                                }}
                                key={value.value}
                                onClick={() =>
                                    toggleFilter(value, valueFilter.attributeId, isSelected)
                                }
                                aria-checked={isSelected}
                                variant="outline"
                                aria-label={formatMessage(
                                    isSelected ? REMOVE_FILTER : ADD_FILTER,
                                    value
                                )}
                            >
                                <Center
                                    {...styles.swatchButton}
                                    className="refinements-size-swatch-button"
                                    color={isSelected ? 'webTypo.textOnColor' : 'webTypo.textPrimary'}
                                >
                                    {value.label}
                                </Center>
                            </Button>
                        )
                    })}
                </SimpleGrid>
            </Box>
        )
    })
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
        PropTypes.object
    ]),
    toggleFilter: PropTypes.func
}

export default SizeRefinements
